import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { getFields, deleteField, addField } from '../services/encryption';
import getAdminFieldsService from '../services/adminFields';

const EncryptedFields = () => {
  const [passwordPop, setPasswordPop] = useState(true);
  const [password, setPassword] = useState('');
  const [fields, setFields] = useState([]);
  const [encryptedFields, setEncryptedFields] = useState([]);
  const [newFieldName, setNewFieldName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchFields();
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetchEncryptedFields();
      setPasswordPop(false);
    } catch (error) {
      setEncryptedFields([]);
      window.alert('Failed to fetch encrypted fields', error.message);
    }
  };

  const fetchFields = async () => {
    try {
      const fields = await getAdminFieldsService.getAdminFields();
      setFields(fields);
    } catch (error) {
      setFields([]);
      window.alert('Failed to fetch fields');
    }
  };

  const fetchEncryptedFields = async () => {
    try {
      const encryptedFieldsResponse = await getFields(password);
      setEncryptedFields(encryptedFieldsResponse?.fields || []);
    } catch (error) {
      setEncryptedFields([]);
      window.alert('Failed to fetch encrypted fields');
    }
  };

  const handleDeleteField = async (field) => {
    const formData = {
      fieldName: field.fieldName,
      password: password,
    };

    try {
      const response = await deleteField(formData);
      if (response?.message) {
        setEncryptedFields((prevFields) =>
          prevFields.filter((f) => f.fieldName !== field.fieldName)
        );
        window.alert(response.message);
      }
      if (response.error) {
        window.alert(response.error || 'Something went wrong');
      }
    } catch (error) {
      window.alert('Failed to delete field');
    }
  };

  const handleAddField = async () => {
    if (!newFieldName.trim()) {
      window.alert('Field name cannot be empty');
      return;
    }

    const formData = {
      fieldName: newFieldName,
      password: password,
    };

    try {
      const response = await addField(formData);
      if (response?.message) {
        setEncryptedFields((prevFields) => [
          ...prevFields,
          { fieldName: newFieldName },
        ]);
        setNewFieldName('');
        window.alert(response.message);
      }
    } catch (error) {
      window.alert('Failed to add field');
    }
  };

  const handleClosePasswordPopup = () => {
    setPassword('');
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] p-6">
      {passwordPop && (
        <div className="password-popup">
          <h2>Enter Master Password</h2>

          <span
            onClick={handleClosePasswordPopup}
            role="button"
            aria-label="Close"
            className="
                  absolute
                  top-3
                  right-3
                  text-gray-600
                  text-xl
                  cursor-pointer
                  hover:text-gray-800
                  select-none
                "
          >
            ✕
          </span>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              placeholder="Master Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button type="submit">Submit</button>
          </form>
        </div>
      )}

      {!passwordPop && (
        <div
          className="
          relative
          max-w-[360px]
          sm:max-w-xl
          md:max-w-2xl
          lg:max-w-3xl
          mx-auto
          bg-white
          rounded-xl
          shadow-sm
          border
          border-gray-200
          p-5
          sm:p-6
        "
        >
          <button
            onClick={() => navigate(-1)}
            aria-label="Close"
            className="
              absolute
              top-3
              right-3
              p-1.5
              rounded-md
              text-gray-500
              hover:text-gray-800
              hover:bg-gray-100
              transition
            "
          >
            <X size={18} />
          </button>

          <h1
            className="
            text-lg
            sm:text-xl
            font-semibold
            text-gray-800
            mb-5
            text-center
          "
          >
            Encrypted Fields
          </h1>

          {/* Add Field Section */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <select
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                className="w-full sm:flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm
                          focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a field</option>
                {[
                  ...new Set([
                    ...fields.map((f) => f.name),
                    'fullName',
                    'type',
                    'city',
                    'zip',
                    'state',
                    'currentPremium',
                    'expectedPremium',
                    'startDate',
                    'currentProvider',
                    'description',
                  ]),
                ].map((fieldName, index) => (
                  <option key={index} value={fieldName}>
                    {fieldName}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAddField}
                disabled={!newFieldName}
                className="w-full sm:w-auto px-4 py-2 rounded-lg
                          bg-blue-600 text-white text-sm font-medium
                          hover:bg-blue-700 transition
                          disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Field
              </button>
            </div>
          </div>
          {/* Encrypted Fields List */}
          {encryptedFields.length > 0 ? (
            <ul className="space-y-2">
              {encryptedFields.map((field, index) => (
                <li
                  key={index}
                  className="flex justify-between items-center bg-gray-50 border border-gray-200 rounded-lg px-4 py-2"
                >
                  <span className="text-sm text-gray-800">
                    {field.fieldName}
                  </span>
                  <button
                    onClick={() => handleDeleteField(field)}
                    className="px-3 py-1.5 rounded-md bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No encrypted fields found.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default EncryptedFields;
